import CheckmarkIconOrange from "@/assets/images/icons/checkmark_icon_orange.svg";
import { CatalogSkillLevelPill } from "@/components/courses/CatalogSkillLevelPill";
import { COURSE_THUMBNAIL_ASPECT } from "@/components/ui/CourseThumbnailCard";
import { CaptionText, CardTitle, EyebrowText } from "@/components/ui/Typography";
import { BRAND_COLORS } from "@/lib/brandColors";
import type { Course } from "@/types";
import { Image } from "expo-image";
import { Link } from "expo-router";
import React from "react";
import { Platform } from "react-native";
import { XStack, YStack } from "tamagui";

type Props = {
  course: Course;
  href: string;
  width: number;
  thumbnailUri?: string | null;
  /** 0–100 */
  progressPercentage?: number;
  progressBarHeight?: number;
  showSummary?: boolean;
  isMobile: boolean;
  /** Runs before navigation (e.g. search-click analytics). */
  onPress?: () => void;
};


/** Catalog grid card — thumbnail + progress bar + title/meta like search rows. */
export const CoursesCatalogCard = React.memo(function CoursesCatalogCard({
  course,
  href,
  width,
  thumbnailUri,
  progressPercentage = 0,
  progressBarHeight = 4,
  showSummary = true,
  isMobile,
  onPress,
}: Props) {
  const showSummaryBlock = showSummary && !isMobile;
  const progress = Math.min(100, Math.max(0, progressPercentage));
  const showCompletedCheck = progress >= 100;
  const instructorNames =
    course.instructor_names?.filter(Boolean).join(" & ") ?? "";

  return (
    <Link href={href as any} asChild onPress={onPress}>
      <YStack
        w={width}
        f={1}
        br={10}
        overflow="hidden"
        borderWidth={1}
        borderColor="rgba(0,0,0,0.08)"
        bg={BRAND_COLORS.cardBg}
        accessibilityLabel={course.title}
        boxShadow="0 2px 8px rgba(0,0,0,0.06), 0 0 1px rgba(0,0,0,0.04)"
        style={{ cursor: Platform.OS === "web" ? "pointer" : undefined }}
        hoverStyle={{
          borderColor: "rgba(0,0,0,0.12)",
          boxShadow: "0 10px 28px rgba(0,0,0,0.1), 0 0 1px rgba(0,0,0,0.06)",
        }}
        pressStyle={{ scale: 0.99, opacity: 0.96 }}
      >
        <YStack position="relative" w="100%" overflow="hidden">
          {thumbnailUri ? (
            <Image
              source={{ uri: thumbnailUri }}
              style={{ width: "100%", aspectRatio: COURSE_THUMBNAIL_ASPECT, backgroundColor: "#eee" }}
              contentFit="cover"
              recyclingKey={thumbnailUri}
            />
          ) : (
            <YStack w="100%" aspectRatio={COURSE_THUMBNAIL_ASPECT} bg="#E8E4DF" />
          )}
          {showCompletedCheck ? (
            <YStack position="absolute" top={8} right={8} zIndex={1}>
              <CheckmarkIconOrange width={16} height={16} />
            </YStack>
          ) : null}
        </YStack>

        <XStack h={progressBarHeight} backgroundColor="#D2CCC5" overflow="hidden">
          <YStack
            h="100%"
            backgroundColor="$accent"
            borderRadius={30}
            w={`${progress}%`}
          />
        </XStack>

        <YStack p="$3" gap="$2" minHeight={showSummaryBlock ? 72 : undefined}>
          <XStack ai="flex-start" jc="space-between" gap="$2" minWidth={0}>
            <CardTitle
              f={1}
              color={BRAND_COLORS.text}
              minWidth={0}
            >
              {course.title}
            </CardTitle>
            {course.skill_level != null ? (
              <CatalogSkillLevelPill level={course.skill_level} />
            ) : null}
          </XStack>

          {instructorNames ? (
            <EyebrowText color={BRAND_COLORS.instructorMeta} numberOfLines={1}>
              {instructorNames}
            </EyebrowText>
          ) : null}

          {showSummaryBlock && course.summary ? (
            <CaptionText color={BRAND_COLORS.muted} numberOfLines={2}>
              {course.summary}
            </CaptionText>
          ) : null}
        </YStack>
      </YStack>
    </Link>
  );
});
